import { useMemo, useRef } from 'react';
import { AnalyticsFilterBar } from '@/components/analytics/AnalyticsFilterBar';
import { WidgetCard } from '@/components/analytics/WidgetCard';
import { KpiWithDelta } from '@/components/analytics/KpiWithDelta';
import { ExportPdfTrigger } from '@/components/analytics/ExportPdfTrigger';
import { Chart } from '@/components/charts/Chart';
import { SectionHeader } from '@/components/analytics/SectionHeader';
import { Badge } from '@/components/ui/badge';
import {
  useSecuritySummary,
  useFirewallHitsByNode,
  useRulesPerNetwork,
  useDeniedTrafficTrend,
} from '@/hooks/useSecurity';
import { useDashboardFilters } from '@/hooks/useDashboardFilters';
import {
  ComposedChart,
  Line as RechartsLineCmp,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { PIE_COLORS } from '@/utils/chart-colors';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';
import type { SeriesDatum } from '@/components/charts/types';
import { downloadCsv } from '@/utils/csv';
import { exportFilename } from '@/utils/pdf';
import { useUIStore } from '@/stores/uiStore';
import type { CategoricalDatum } from '@/components/charts/types';

/**
 * Analytics — Security Policies (MVP)
 *
 * GoodData "4 Security Policies" replacement. Pure SE data — every widget
 * reads existing hooks (no new Neeve-API calls). First full dashboard build,
 * proves the primitives + chart abstraction compose end-to-end.
 *
 * Widget map vs MVP_WIDGET_CATALOG.md:
 *   4.1 Count of Rules      → KpiWithDelta x3 (Total / per Node / per Network)
 *   4.2 Rules Per Network   → placeholder (stacked-bar lands Week 3)
 *   4.3 Denied Packets+Bytes by Month → placeholder (dual-axis line lands Week 3)
 *   4.4 Denied Packets by Node → Chart type='bar' (slicer-driven, all 4 libs)
 */
export function SecurityPoliciesAnalytics() {
  const { summary, loading } = useSecuritySummary();
  const { data: hitsByNode, loading: hitsLoading } = useFirewallHitsByNode();
  const { data: rulesPerNetwork, loading: rulesLoading } = useRulesPerNetwork();
  const { dateRange } = useDashboardFilters();
  // Map the filter-bar relative range to the period token the existing
  // denied-trend endpoint accepts ('24h' | '7d' | '30d' | '90d'). Wider
  // ranges (year+) clamp to '90d' until the backend per-month aggregator
  // lands; "All time" defaults to 30d for backwards compat.
  const trendPeriod: '24h' | '7d' | '30d' | '90d' = useMemo(() => {
    if (dateRange.kind !== 'relative') return '30d';
    const days =
      dateRange.unit === 'hour' ? Math.max(1, Math.ceil(dateRange.count / 24)) :
      dateRange.unit === 'day' ? dateRange.count :
      dateRange.unit === 'week' ? dateRange.count * 7 :
      dateRange.unit === 'month' ? dateRange.count * 30 :
      dateRange.unit === 'quarter' ? dateRange.count * 90 :
      dateRange.unit === 'year' ? dateRange.count * 365 :
      30;
    if (days <= 1) return '24h';
    if (days <= 7) return '7d';
    if (days <= 30) return '30d';
    return '90d';
  }, [dateRange]);
  const { data: deniedTrend, loading: deniedTrendLoading } = useDeniedTrafficTrend(trendPeriod);
  const session = useUIStore((s) => s.session);

  // 4.4 Denied Packets by Node — top 20 by deniedPackets, descending.
  const deniedByNode = useMemo<CategoricalDatum[]>(() => {
    return [...hitsByNode]
      .map((h) => ({ label: h.nodeName, value: Number(h.deniedPackets) || 0, rawBytes: h.deniedBytes }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 20);
  }, [hitsByNode]);

  // 4.2 Rules Per Network — aggregate allow/deny rule counts per node, top 25
  // by total rule count. Matches the GoodData stacked-bar widget (x=node,
  // stack=allow/deny, y=# of rules).
  const rulesPerNodeStack = useMemo<SeriesDatum[]>(() => {
    const byNode = new Map<string, { Allow: number; Deny: number }>();
    for (const row of rulesPerNetwork) {
      const node = row.nodeName ?? row.name;
      const acc = byNode.get(node) ?? { Allow: 0, Deny: 0 };
      acc.Allow += row.allowRules;
      acc.Deny += row.denyRules;
      byNode.set(node, acc);
    }
    return Array.from(byNode.entries())
      .map(([node, counts]) => ({ x: node, Allow: counts.Allow, Deny: counts.Deny }))
      .sort((a, b) => (Number(b.Allow) + Number(b.Deny)) - (Number(a.Allow) + Number(a.Deny)))
      .slice(0, 25);
  }, [rulesPerNetwork]);

  // 4.3 Denied Packets + Bytes by Month — dual-axis: Bytes (left) + Packets (right).
  // Uses 30d daily buckets from useDeniedTrafficTrend (the existing hook).
  // GoodData renders this as monthly bars over ~21 months; matching that
  // window requires a backend per-month aggregator (Week 3+). For now we show
  // what's available — clearly labeled in the info tooltip.
  const deniedByMonth = useMemo(
    () =>
      deniedTrend.map((p) => ({
        bucket: p.bucket,
        // null = no snapshots for this bucket → gap, not zero
        bytes: p.deniedBytes === null ? null : Number(p.deniedBytes) || 0,
        packets: p.deniedPackets === null ? null : Number(p.deniedPackets) || 0,
      })),
    [deniedTrend]
  );

  const handleExportDeniedByNodeCsv = () => {
    if (deniedByNode.length === 0) return;
    downloadCsv(
      deniedByNode.map((d) => ({ Node: d.label, 'Denied Packets': d.value, 'Denied Bytes': d.rawBytes ?? '' })),
      exportFilename('analytics-denied-packets-by-node', 'csv'),
      { report: 'Analytics — Denied Packets by Node' }
    );
  };

  const pdfTargetRef = useRef<HTMLDivElement>(null);

  return (
    <div className="dls-space-y-4">
      <div className="dls-flex dls-items-center dls-justify-between">
        <div>
          <h1 className="dls-text-2xl dls-font-bold dls-text-foreground">Security Policies</h1>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <Badge variant="outline" className="dls-text-muted-foreground">
            Secure Edge
          </Badge>
          <ExportPdfTrigger
            title={`Security Policies${session?.tenant?.name ? ` — ${session.tenant.name}` : ''}`}
            filenameSlug="security-policies"
            targetRef={pdfTargetRef}
          />
        </div>
      </div>

      <AnalyticsFilterBar />
      <p className="dls-text-xs dls-text-muted-foreground dls-px-1 dls-italic">
        Date range applies to time-series widgets (Denied Packets and Bytes). Rule counts and per-node totals are point-in-time snapshots.
      </p>

      {/* PDF capture target — everything inside this div is what gets exported. */}
      <div ref={pdfTargetRef} className="dls-space-y-4">

      <SectionHeader
        title="Secure Edge — Security Policies"
        description={
          'Firewall coverage, rule distribution, and denied-traffic posture across the fleet. "Allow" and "Deny" ' +
          'rule counts roll up per node from the per-network firewall policies. Denied Packets and Bytes by Month ' +
          'shows the cumulative denied volume over the selected window — useful for spotting nodes under sustained ' +
          'scanning or misconfigured outbound traffic.'
        }
      />

      {/* 4.1 Count of Rules — KPI triplet inside one card */}
      <WidgetCard
        title="Count of Rules"
        info="Total firewall rules deployed across the fleet, with averages per node and per network."
      >
        <div className="dls-grid dls-grid-cols-1 md:dls-grid-cols-3 dls-gap-6">
          <KpiWithDelta
            value={loading ? '—' : summary?.totalFirewallRules ?? 0}
            subtitle="Total Rules"
          />
          <KpiWithDelta
            value={loading ? '—' : (summary?.rulesPerNode ?? 0).toFixed(2)}
            subtitle="Rules per Node"
          />
          <KpiWithDelta
            value={loading ? '—' : (summary?.rulesPerNetwork ?? 0).toFixed(2)}
            subtitle="Rules per Network"
          />
        </div>
      </WidgetCard>

      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-2 dls-gap-4">
        {/* 4.2 Rules Per Network — Stacked Bar (allow + deny per node, top 25). */}
        <WidgetCard
          title="Rules Per Network"
          info="Allow vs Deny rule distribution per node. Shows the 25 nodes with the most rules. Renders via the active chart library (slicer-aware)."
        >
          {rulesLoading ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">Loading…</div>
          ) : rulesPerNodeStack.length === 0 ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">No data</div>
          ) : (
            <Chart
              type="stacked-bar"
              data={rulesPerNodeStack}
              xKey="x"
              seriesKeys={['Allow', 'Deny']}
              xLabel="Node Name"
              yLabel="# of Rules"
              height={280}
              ariaLabel="Rules per network — allow + deny stacked per node"
            />
          )}
        </WidgetCard>

        {/* 4.3 Denied Packets + Bytes — dual-axis line (bytes left, packets right).
            One-off Recharts ComposedChart here because the Chart abstraction
            doesn't yet expose dual-axis support — the slicer can adopt this
            shape once a combo/dual-axis chart type lands (Week 3+). */}
        <WidgetCard
          title="Denied Packets and Bytes by Month"
          info="Trend over the most recent 30 days. Bytes plotted against the left axis (teal); Packets against the right axis (orange). Backend per-month aggregator for the full 21-month GoodData window is a Week 3 follow-up — this widget will switch to it without UI change."
        >
          {deniedTrendLoading ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">Loading…</div>
          ) : deniedByMonth.length === 0 ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={deniedByMonth} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="bytes"
                  orientation="left"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${(Number(v) / 1e9).toFixed(1)}G`}
                  label={{ value: 'Bytes', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                />
                <YAxis
                  yAxisId="packets"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${(Number(v) / 1e3).toFixed(0)}K`}
                  label={{ value: 'Packets', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
                />
                <RechartsTooltip
                  content={({ active, payload, label }) =>
                    active && payload && payload.length > 0 ? (
                      <DrillAwareTooltip
                        header={String(label ?? '')}
                        rows={payload.map((p, i) => ({
                          color: (p.color as string) ?? PIE_COLORS[i % PIE_COLORS.length],
                          label: String(p.name ?? p.dataKey ?? ''),
                          value: Number(p.value).toLocaleString(),
                        }))}
                      />
                    ) : null
                  }
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <RechartsLineCmp
                  yAxisId="bytes"
                  type="monotone"
                  dataKey="bytes"
                  name="Bytes"
                  stroke={PIE_COLORS[0]}
                  strokeWidth={2}
                  dot={false}
                />
                <RechartsLineCmp
                  yAxisId="packets"
                  type="monotone"
                  dataKey="packets"
                  name="Packets"
                  stroke={PIE_COLORS[6]}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </WidgetCard>
      </div>

      {/* 4.4 Denied Packets by Node — slicer-driven via Chart abstraction */}
      <WidgetCard
        title="Denied Packets by Node (Top 20)"
        info="Top 20 nodes by denied packet count. Renders via the active chart library (Recharts default; dev users can swap to Visx / D3 / ECharts in Dev Console)."
        onExportCsv={deniedByNode.length > 0 ? handleExportDeniedByNodeCsv : undefined}
      >
        {hitsLoading ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">
            Loading…
          </div>
        ) : (
          <Chart
            type="bar"
            data={deniedByNode}
            orientation="horizontal"
            height={400}
            ariaLabel="Denied packets by node, top 20"
            data-testid="denied-packets-by-node"
          />
        )}
      </WidgetCard>
      </div>
    </div>
  );
}
